import api from '../../services/api';

/**
 * Call an admin AI endpoint with a paid-fallback confirmation gate.
 *
 * Flow:
 *  - Backend tries free first. If free fails and admin, it returns 402 with
 *    { errors: { free_failed: true, reason } } instead of burning paid tokens silently.
 *  - Frontend catches the 402, asks the admin (via askPaidConfirmation), and
 *    retries with { confirm_paid: true } if the admin agrees.
 *  - Once confirmed once, paidConfirmedRef.current is set so subsequent calls in
 *    the same batch skip the prompt and send confirm_paid: true directly.
 *
 * Throws `Error('__cancelled__')` if admin declines the paid retry — callers
 * should detect this and abort the batch cleanly.
 *
 * @param {string} url — API endpoint (e.g. '/admin/improve-question.php')
 * @param {object} body — request body (without confirm_paid)
 * @param {{ current: boolean }} paidConfirmedRef — batch-local flag
 * @param {(reason: string) => Promise<boolean>} askPaidConfirmation
 * @returns {Promise<object>} API response payload (data from axios)
 */
export async function callAiWithPaidGate(url, body, paidConfirmedRef, askPaidConfirmation) {
    const firstBody = paidConfirmedRef.current ? { ...body, confirm_paid: true } : body;
    try {
        const { data } = await api.post(url, firstBody);
        return data;
    } catch (err) {
        const errData = err.response?.data;
        if (err.response?.status === 402 && errData?.errors?.free_failed) {
            const confirmed = await askPaidConfirmation(errData.errors.reason || '');
            if (!confirmed) throw new Error('__cancelled__');
            paidConfirmedRef.current = true;
            const { data } = await api.post(url, { ...body, confirm_paid: true });
            return data;
        }
        throw err;
    }
}
